/* eslint-disable no-empty, no-console, no-unused-vars */
class HybridReviews {
  constructor() {
    this.supabaseUrl = window.HYBRID_REVIEWS_CONFIG?.supabaseUrl;
    this.supabaseKey = window.HYBRID_REVIEWS_CONFIG?.supabaseKey;
    this.debug = window.HYBRID_REVIEWS_CONFIG?.debug || false;
    this.initialized = false;
    this.cache = new Map();
    this.CACHE_TTL = 5 * 60 * 1000;
    this.MAX_CACHE_ENTRIES = 50;
    this.productStats = {};
    this.currentPage = 1;
    this.hasMoreReviews = false;
    this.currentProductId = null;
    this.currentSort = 'newest';

    this.errorLog = [];
    this.MAX_ERRORS = 10;

    this.ANIMATION_DELAY_SHORT = 100;
    this.ANIMATION_DELAY_MEDIUM = 300;
    this.ANIMATION_DELAY_LONG = 500;
    this.REVIEWS_PER_PAGE = 5;
    this.MAX_RATING = 5;
    this.PAGINATION_PAGE_BUFFER = 3;

    this.domCache = {
      widget: null
    };

    this.injectAnimationCSS();
  }

  injectAnimationCSS() {
    if (document.getElementById('hybrid-reviews-animation-css')) return;

    const style = document.createElement('style');
    style.id = 'hybrid-reviews-animation-css';
    style.textContent = `
      .jdgm-rev-widg__reviews {
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
      }

      .jdgm-rev-widg__reviews.hybrid-collapsing {
        opacity: 0;
        transform: scaleY(0.95);
        transform-origin: top;
      }

      .jdgm-rev-widg__reviews.hybrid-expanding {
        opacity: 0;
        transform: scaleY(0.95);
        transform-origin: top;
      }
    `;
    document.head.appendChild(style);
  }

  getWidget() {
    if (!this.domCache.widget || !document.contains(this.domCache.widget)) {
      this.domCache.widget = document.querySelector('[data-hybrid-widget="true"]');
    }
    return this.domCache.widget;
  }

  clearDomCache() {
    this.domCache = {
      widget: null
    };
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  setCachedData(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });

    if (this.cache.size > this.MAX_CACHE_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  logError(error, context = {}) {
    const errorInfo = {
      timestamp: new Date().toISOString(),
      message: error.message || String(error),
      stack: error.stack,
      ...context
    };

    this.errorLog.push(errorInfo);
    if (this.errorLog.length > this.MAX_ERRORS) {
      this.errorLog.shift();
    }

    if (this.debug || context.severity === 'critical') {
      console.error('[HybridReviews]', context.method || 'unknown', error);
      console.error('Context:', context);
    }
  }

  async _wrapApiCall(cacheKey, fetchFn, defaultValue, errorContext) {
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      return { success: true, data: cached };
    }

    try {
      const data = await fetchFn();
      this.setCachedData(cacheKey, data);
      return { success: true, data };
    } catch (error) {
      this.logError(error, { severity: 'high', ...errorContext });
      return { success: false, data: defaultValue };
    }
  }

  async init(productId, productHandle) {
    if (!this.supabaseUrl || !this.supabaseKey) {
      return;
    }

    try {
      const widget = this.getWidget();

      this.currentProductId = productId;
      this.currentProductHandle = productHandle;

      this.handleJudgeMeEmailLink(widget);

      const statsResult = await this.fetchProductStats(productId, true);
      const reviewsResult = await this.fetchProductReviews(productId, {}, true);

      const apiSuccess = statsResult.success && reviewsResult.success;

      this.productStats[productId] = statsResult.data;

      await this.populateWidget(productId, reviewsResult.data, statsResult.data, false, apiSuccess);

      this.populatePreviewBadge(productId, statsResult.data);

      window.setTimeout(() => {
        this.cleanupDropdownOptions(widget);
        this.initializeEventListeners(productId, widget);
      }, 500);

      this.initialized = true;
    } catch (error) {
      this.logError(error, {
        method: 'init',
        productId: productId,
        productHandle: productHandle,
        severity: 'critical'
      });
    }
  }

  handleJudgeMeEmailLink(widget) {
    if (!widget) return;

    const hash = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);

    const hasJudgeMeParams =
      hash === '#judgeme_product_reviews' ||
      urlParams.has('token') ||
      urlParams.has('review_token') ||
      urlParams.has('email');

    if (!hasJudgeMeParams) return;

    if (hash === '#judgeme_product_reviews') {
      setTimeout(() => {
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }

    const token = urlParams.get('token') || urlParams.get('review_token');
    const email = urlParams.get('email');
    const name = urlParams.get('name');
    const orderId = urlParams.get('order_id');

    if (token) {
      this.verificationToken = token;
      this.isVerifiedReview = true;
    }

    if (orderId) {
      this.orderId = orderId;
    }

    if (token || email) {
      setTimeout(() => {
        const formWrapper = widget.querySelector('.jdgm-form-wrapper');
        const writeReviewLink = widget.querySelector('.jdgm-write-rev-link');
        const form = widget.querySelector('.jdgm-form');

        if (formWrapper) {
          formWrapper.style.display = 'block';

          if (writeReviewLink) {
            writeReviewLink.setAttribute('aria-expanded', 'true');
          }

          if (token && form) {
            form.classList.add('jdgm--verified-form');

            const emailLabel = widget.querySelector('label[for*="reviewer_email"]');
            const emailField = widget.querySelector('input[name="reviewer_email"]');

            if (emailLabel) {
              emailLabel.textContent = 'Verified Email (private, can not be edited)';
            }

            if (emailField) {
              emailField.readOnly = true;
              emailField.setAttribute('readonly', 'readonly');
              emailField.style.backgroundColor = '#f5f5f5';
              emailField.style.cursor = 'not-allowed';
            }

            const submitButton = widget.querySelector('.jdgm-submit-rev');
            if (submitButton) {
              submitButton.value = 'Submit Verified Review';
            }
          }

          if (email) {
            const emailField = widget.querySelector('input[name="reviewer_email"]');
            if (emailField) {
              emailField.value = decodeURIComponent(email);
            }
          }

          if (name) {
            const nameField = widget.querySelector('input[name="reviewer_name"]');
            if (nameField) {
              nameField.value = decodeURIComponent(name);
            }
          }

          this.attachStarRatingHandlers(widget);

          const ratingStars = widget.querySelector('.jdgm-form__rating');
          if (ratingStars) {
            const firstStar = ratingStars.querySelector('[data-alt="1"]');
            if (firstStar) {
              firstStar.focus();
            }
          }
        }
      }, 600);
    }
  }

  fixHistogramBarColors() {
    const styleId = 'hybrid-histogram-fix';

    const existingStyle = document.getElementById(styleId);
    if (existingStyle) existingStyle.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .jdgm-histogram__bar-content {
        background-color: #80839F !important;
      }
    `;

    document.head.appendChild(style);
  }

  customizeDropdownOptions() {
    const sortDropdown = document.querySelector('.jdgm-sort-dropdown');
    if (!sortDropdown) return;

    const unwantedOptions = ['with-pictures', 'pictures-first', 'videos-first'];

    Array.from(sortDropdown.options).forEach(option => {
      if (unwantedOptions.includes(option.value)) {
        option.remove();
      } else {
        switch (option.value) {
          case 'highest-rating':
            option.textContent = 'Best';
            break;
          case 'lowest-rating':
            option.textContent = 'Worst';
            break;
        }
      }
    });
  }

  async fetchProductStats(productId, returnWrapper = false) {
    const emptyStats = { total_reviews: 0, average_rating: 0, rating_distribution: {} };

    const result = await this._wrapApiCall(
      `stats-${productId}`,
      async () => {
        const response = await fetch(`${this.supabaseUrl}/rest/v1/review_stats?product_id=eq.${productId}&select=*`, {
          headers: {
            apikey: this.supabaseKey,
            Authorization: `Bearer ${this.supabaseKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const rawStats = data[0];
          return {
            total_reviews: rawStats.published_reviews || 0,
            average_rating: rawStats.average_rating || 0,
            rating_distribution: {
              1: rawStats.rating_1_count || 0,
              2: rawStats.rating_2_count || 0,
              3: rawStats.rating_3_count || 0,
              4: rawStats.rating_4_count || 0,
              5: rawStats.rating_5_count || 0
            }
          };
        }

        return emptyStats;
      },
      emptyStats,
      { method: 'fetchProductStats', productId, url: `${this.supabaseUrl}/rest/v1/review_stats` }
    );

    return returnWrapper ? result : result.data;
  }

  async fetchProductReviews(productId, options = {}, returnWrapper = false) {
    const { limit = this.REVIEWS_PER_PAGE, page = 1, sort = 'newest', filter = null, forceRefresh = false } = options;
    const offset = (page - 1) * limit;
    const cacheKey = `reviews-${productId}-${sort}-${filter}-${page}`;

    if (forceRefresh) {
      this.cache.delete(cacheKey);
    }

    const result = await this._wrapApiCall(
      cacheKey,
      async () => {
        let query = `product_id=eq.${productId}&status=eq.published`;

        if (filter && filter !== 'all') {
          query += `&rating=eq.${filter}`;
        }

        let orderBy = '';
        switch (sort) {
          case 'oldest':
            orderBy = 'created_at.asc';
            break;
          case 'highest-rating':
            orderBy = 'rating.desc,created_at.desc';
            break;
          case 'lowest-rating':
            orderBy = 'rating.asc,created_at.desc';
            break;
          case 'newest':
          default:
            orderBy = 'created_at.desc';
            break;
        }

        query += `&order=${orderBy}&limit=${limit + 1}&offset=${offset}`;

        const response = await fetch(`${this.supabaseUrl}/rest/v1/reviews?${query}`, {
          headers: {
            apikey: this.supabaseKey,
            Authorization: `Bearer ${this.supabaseKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reviews = await response.json();

        this.hasMoreReviews = reviews.length > limit;

        return reviews.slice(0, limit);
      },
      [],
      { method: 'fetchProductReviews', productId, options, url: `${this.supabaseUrl}/rest/v1/reviews` }
    );

    return returnWrapper ? result : result.data;
  }

  showWidgetWhenReady(widget) {
    if (!widget) return;

    window.setTimeout(() => {
      const revWidg = widget.querySelector('.jdgm-rev-widg');
      if (revWidg) {
        const computedStyle = window.getComputedStyle(revWidg);

        if (computedStyle.display !== 'none') {
          widget.style.display = 'block';
          return;
        }
      }

      window.setTimeout(() => {
        widget.style.display = 'block';
      }, 500);
    }, 100);
  }

  async populateWidget(productId, reviews, stats, append = false, apiSuccess = true) {
    const widget = this.getWidget();
    if (!widget) {
      return;
    }

    this.currentProductId = productId;

    const reviewsContainer = widget.querySelector('.jdgm-rev-widg__reviews');
    if (!append && reviewsContainer && this.initialized) {
      reviewsContainer.classList.add('hybrid-collapsing');
      await new Promise(resolve => setTimeout(resolve, this.ANIMATION_DELAY_MEDIUM));
    }

    if (!append) {
      this.updateWidgetSummary(widget, stats);
    }

    this.renderReviewList(widget, reviews, append, reviewsContainer);

    if (!append && reviewsContainer && this.initialized) {
      reviewsContainer.classList.remove('hybrid-collapsing');
      reviewsContainer.classList.add('hybrid-expanding');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          reviewsContainer.classList.remove('hybrid-expanding');
        });
      });
    }

    if (!append) {
      this.renderPagination(widget, productId, stats);
    }

    this.finalizeWidgetDisplay(widget, stats);

    // Add monitoring marker only if API calls succeeded
    if (!append && apiSuccess) {
      widget.setAttribute('data-hybrid-populated', Date.now());
    }
  }

  updateWidgetSummary(widget, stats) {
    const summaryText = widget.querySelector('.jdgm-rev-widg__summary-text');
    if (summaryText) {
      if (stats.total_reviews === 0) {
        summaryText.innerHTML = 'Be the first to write a review';
      } else {
        const reviewText = stats.total_reviews === 1 ? 'review' : 'reviews';
        const verifiedCheckmark =
          '<img class="jdgm-verified-checkmark" src="https://judgeme-public-images.imgix.net/judgeme/logos/verified-checkmark.svg" alt="Verified Checkmark">';
        summaryText.innerHTML = `Based on ${stats.total_reviews} ${reviewText} ${verifiedCheckmark}`;
      }
    }

    this.updateStarDisplay(widget.querySelector('.jdgm-rev-widg__summary-stars'), stats.average_rating);

    this.updateHistogram(widget, stats);
  }

  renderReviewList(widget, reviews, append, reviewsContainer) {
    if (!reviewsContainer) {
      reviewsContainer = widget.querySelector('.jdgm-rev-widg__reviews');
    }

    if (!reviewsContainer) return;

    if (reviews.length === 0 && !append) {
      reviewsContainer.innerHTML = '';
    } else {
      const reviewsHtml = reviews.map(review => this.renderReview(review)).join('');
      if (append) {
        reviewsContainer.insertAdjacentHTML('beforeend', reviewsHtml);
      } else {
        reviewsContainer.innerHTML = reviewsHtml;
      }
    }
  }

  finalizeWidgetDisplay(widget, stats) {
    const widgetBody = widget.querySelector('.jdgm-rev-widg__body');
    if (widgetBody) {
      widgetBody.style.display = 'block';
    }

    const revWidg = widget.querySelector('.jdgm-rev-widg');
    if (revWidg) {
      revWidg.setAttribute('data-average-rating', (stats.average_rating || 0).toFixed(2));
      revWidg.setAttribute('data-number-of-reviews', stats.total_reviews || 0);
    }
  }

  populatePreviewBadge(_productId, stats) {
    const badges = document.querySelectorAll('.hybrid-preview-badge');

    badges.forEach(badgeWrapper => {
      const badge = badgeWrapper.querySelector('.hybrid-prev-badge');
      if (!badge) return;

      const starsEl = badge.querySelector('.jdgm-prev-badge__stars');
      if (starsEl) {
        this.updateStarDisplay(starsEl, stats.average_rating, false);
        starsEl.setAttribute('data-score', stats.average_rating || 0);
        starsEl.setAttribute('aria-label', `${stats.average_rating || 0} stars`);
      }

      const textEl = badge.querySelector('.jdgm-prev-badge__text');
      if (textEl) {
        if (stats.total_reviews === 0) {
          textEl.textContent = 'No reviews';
        } else {
          const reviewText = stats.total_reviews === 1 ? 'review' : 'reviews';
          textEl.textContent = `${stats.total_reviews} ${reviewText}`;
        }
      }

      badge.setAttribute('data-average-rating', stats.average_rating || 0);
      badge.setAttribute('data-number-of-reviews', stats.total_reviews || 0);

      badge.style.display = 'block';
      badgeWrapper.style.display = 'block';

      if (!badge.dataset.clickHandlerAdded) {
        badge.addEventListener('click', () => {
          const reviewsWidget = this.getWidget();
          if (reviewsWidget) {
            reviewsWidget.scrollIntoView({ behavior: 'smooth' });
          }
        });
        badge.dataset.clickHandlerAdded = 'true';
      }
    });
  }

  renderPagination(widget, productId, stats) {
    const paginateContainer = widget.querySelector('.jdgm-paginate');
    if (!paginateContainer) {
      return;
    }

    const perPage = this.REVIEWS_PER_PAGE;
    const totalReviews = this.currentFilter
      ? stats.rating_distribution?.[this.currentFilter] || 0
      : stats.total_reviews;
    const totalPages = Math.ceil(totalReviews / perPage);

    if (totalPages <= 1) {
      paginateContainer.style.display = 'none';
      return;
    }

    paginateContainer.style.display = 'block';

    const paginationHTML = this.buildPaginationHTML(totalPages);
    paginateContainer.innerHTML = paginationHTML;

    this.attachPaginationHandlers(widget, productId, paginateContainer);
  }

  buildPaginationHTML(totalPages) {
    let html = '';

    if (this.currentPage > 1) {
      html += `<span class="jdgm-paginate__page jdgm-paginate__prev" data-page="${this.currentPage - 1}" style="display: inline-flex; flex-direction: column; align-items: flex-end; gap: 2px;"><span style="line-height: 1;">←</span><span style="font-size: 0.85em; line-height: 1;">Prev</span></span>`;
    }

    const maxVisible = 7;
    let startPage = Math.max(1, this.currentPage - this.PAGINATION_PAGE_BUFFER);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      const firstPageStyle = this.currentPage === 1 ? ' style="font-weight: 700; font-size: 1.05em;"' : '';
      html += `<span class="jdgm-paginate__page${this.currentPage === 1 ? ' jdgm-paginate__page--active' : ''}" data-page="1"${firstPageStyle}>1</span>`;
      if (startPage > 2) {
        html += `<span class="jdgm-paginate__ellipsis">...</span>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === this.currentPage ? ' jdgm-paginate__page--active' : '';
      const activeStyle = i === this.currentPage ? ' style="font-weight: 700; font-size: 1.05em;"' : '';
      html += `<span class="jdgm-paginate__page${activeClass}" data-page="${i}"${activeStyle}>${i}</span>`;
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        html += `<span class="jdgm-paginate__ellipsis">...</span>`;
      }
      const lastPageStyle = this.currentPage === totalPages ? ' style="font-weight: 700; font-size: 1.05em;"' : '';
      html += `<span class="jdgm-paginate__page${this.currentPage === totalPages ? ' jdgm-paginate__page--active' : ''}" data-page="${totalPages}"${lastPageStyle}>${totalPages}</span>`;
    }

    if (this.currentPage < totalPages) {
      html += `<span class="jdgm-paginate__page jdgm-paginate__next" data-page="${this.currentPage + 1}" style="display: inline-flex; flex-direction: column; align-items: flex-start; gap: 2px;"><span style="line-height: 1;">→</span><span style="font-size: 0.85em; line-height: 1;">Next</span></span>`;
    }

    return html;
  }

  attachPaginationHandlers(widget, productId, paginateContainer) {
    paginateContainer.querySelectorAll('.jdgm-paginate__page:not(.jdgm-paginate__page--active)').forEach(pageEl => {
      pageEl.addEventListener('click', async e => {
        const page = parseInt(e.currentTarget.dataset.page);
        if (!page) return;

        await this.handlePageChange(page, productId, widget);
      });
    });
  }

  async handlePageChange(page, productId, widget) {
    this.showSpinner(widget);

    try {
      this.currentPage = page;
      const reviews = await this.fetchProductReviews(productId, {
        page: page,
        limit: this.REVIEWS_PER_PAGE,
        sort: this.currentSort,
        filter: this.currentFilter
      });

      const stats = await this.fetchProductStats(productId);
      await this.populateWidget(productId, reviews, stats, false);

      window.setTimeout(() => {
        this.attachHistogramListeners(productId, widget);
        this.attachSortListener(productId, widget);
      }, 100);

      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      this.logError(error, {
        method: 'handlePageChange',
        page: page,
        productId: productId,
        severity: 'medium'
      });
    } finally {
      this.hideSpinner(widget);
    }
  }

  updateStarDisplay(starContainer, rating, showNumericRating = true) {
    if (!starContainer) return;

    const ratingValue = rating || 0;
    const stars = starContainer.querySelectorAll('.jdgm-star');
    const fullStars = Math.floor(ratingValue);
    const hasHalfStar = ratingValue % 1 >= 0.5;

    stars.forEach((star, index) => {
      if (index < fullStars) {
        star.className = 'jdgm-star jdgm--on';
      } else if (index === fullStars && hasHalfStar) {
        star.className = 'jdgm-star jdgm--half';
      } else {
        star.className = 'jdgm-star jdgm--off';
      }
    });

    starContainer.setAttribute('aria-label', `Average rating is ${ratingValue.toFixed(2)} stars`);

    if (showNumericRating && ratingValue > 0) {
      let summaryAverage = starContainer.querySelector('.jdgm-rev-widg__summary-average');
      if (!summaryAverage) {
        summaryAverage = document.createElement('span');
        summaryAverage.className = 'jdgm-rev-widg__summary-average';
        starContainer.appendChild(summaryAverage);
      }
      summaryAverage.textContent = `${ratingValue.toFixed(2)} out of ${this.MAX_RATING}`;
    } else if (showNumericRating && ratingValue === 0) {
      const summaryAverage = starContainer.querySelector('.jdgm-rev-widg__summary-average');
      if (summaryAverage) {
        summaryAverage.remove();
      }
    }
  }

  updateHistogram(widget, stats) {
    if (!widget || !stats) return;

    const histogram = widget.querySelector('.jdgm-histogram');
    if (!histogram) return;

    const totalReviews = stats.total_reviews || 0;
    const distribution = stats.rating_distribution || {};

    for (let rating = this.MAX_RATING; rating >= 1; rating--) {
      const row = histogram.querySelector(`[data-rating="${rating}"]`);
      if (!row) continue;

      const count = distribution[rating] || 0;
      const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;

      row.setAttribute('data-frequency', count);
      row.setAttribute('data-percentage', percentage);

      const frequencyEl = row.querySelector('.jdgm-histogram__frequency');
      if (frequencyEl) {
        frequencyEl.textContent = count;
      }

      const barContent = row.querySelector('.jdgm-histogram__bar-content');
      if (barContent) {
        barContent.style.width = `${percentage}%`;
      }

      const starEl = row.querySelector('.jdgm-histogram__star');
      if (starEl) {
        const reviewText = count === 1 ? 'review' : 'reviews';
        starEl.setAttribute('aria-label', `${percentage}% (${count}) ${reviewText} with ${rating} star rating`);
      }
    }

    if (totalReviews > 0) {
      histogram.classList.remove('jdgm-temp-hidden');
      histogram.style.display = 'block';
    } else {
      histogram.classList.add('jdgm-temp-hidden');
      histogram.style.display = 'none';
    }
  }

  renderReview(review) {
    const createdAt = new Date(review.created_at);
    const formattedDate = createdAt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const isoDate = createdAt.toISOString().replace('T', ' ').split('.')[0] + ' UTC';

    return `
      <div class="jdgm-rev jdgm-divider-top jdgm--done-setup"
           data-verified-buyer="${review.verified_purchase || false}"
           data-review-id="${review.id}"
           data-review-language=""
           data-product-title=""
           data-product-url=""
           data-thumb-up-count="0"
           data-thumb-down-count="0">
        <div class="jdgm-rev__header">
          <div class="jdgm-row-product"></div>
          <div class="jdgm-row-rating">
            <span class="jdgm-rev__rating" data-score="${review.rating}" tabindex="0" aria-label="${review.rating} star review" role="img">
              ${this.renderStars(review.rating)}
            </span>
            <span class="jdgm-rev__timestamp" data-content="${isoDate}">${formattedDate}</span>
          </div>
          <div class="jdgm-row-profile">
            <div class="jdgm-rev__icon"></div>
            <span class="jdgm-rev__author-wrapper">
              <span class="jdgm-rev__author">${this.escapeHtml(review.customer_name)}</span>
              ${
                review.verified_purchase
                  ? `
                <span class="jdgm-rev__buyer-badge-wrapper">
                  <span class="jdgm-rev__buyer-badge"></span>
                </span>
              `
                  : ''
              }
            </span>
          </div>
          <div class="jdgm-row-extra">
            <span class="jdgm-rev__location"></span>
          </div>
          <div class="jdgm-rev__br"></div>
        </div>
        <div class="jdgm-rev__content">
          ${review.title ? `<b class="jdgm-rev__title">${this.escapeHtml(review.title)}</b>` : '<b class="jdgm-rev__title"></b>'}
          <div class="jdgm-rev__body">
            <p>${this.escapeHtml(review.content)}</p>
          </div>
          <div class="jdgm-rev__custom-form"></div>
          <div class="jdgm-rev__transparency-badge-wrapper"></div>
        </div>
        <div class="jdgm-rev__actions">
          <div class="jdgm-rev__social"></div>
          <div class="jdgm-rev__votes"></div>
        </div>
      </div>
    `;
  }

  renderStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    let starsHtml = '';

    for (let i = 1; i <= this.MAX_RATING; i++) {
      if (i <= fullStars) {
        starsHtml += '<span class="jdgm-star jdgm--on"></span>';
      } else if (i === fullStars + 1 && hasHalfStar) {
        starsHtml += '<span class="jdgm-star jdgm--half"></span>';
      } else {
        starsHtml += '<span class="jdgm-star jdgm--off"></span>';
      }
    }
    return starsHtml;
  }

  async fetchBatchProductStats(productIds) {
    if (!this.supabaseUrl || !this.supabaseKey) {
      return {};
    }

    try {
      const idsParam = productIds.join(',');
      const response = await fetch(`${this.supabaseUrl}/rest/v1/review_stats?product_id=in.(${idsParam})&select=*`, {
        headers: {
          apikey: this.supabaseKey,
          Authorization: `Bearer ${this.supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const statsMap = {};

      data.forEach(rawStat => {
        statsMap[rawStat.product_id] = {
          total_reviews: rawStat.published_reviews || 0,
          average_rating: rawStat.average_rating || 0,
          rating_distribution: {
            1: rawStat.rating_1_count || 0,
            2: rawStat.rating_2_count || 0,
            3: rawStat.rating_3_count || 0,
            4: rawStat.rating_4_count || 0,
            5: rawStat.rating_5_count || 0
          }
        };
      });

      productIds.forEach(id => {
        if (!statsMap[id]) {
          statsMap[id] = { total_reviews: 0, average_rating: 0, rating_distribution: {} };
        }
      });

      return statsMap;
    } catch (_error) {
      return {};
    }
  }

  async initStarRating(productId) {
    try {
      const stats = await this.fetchProductStats(productId);
      this.renderStarRating(productId, stats);
    } catch (_error) {}
  }

  renderStarRating(productId, stats) {
    const ratingElements = [
      document.querySelector(`[data-product-id="${productId}"] .star-rating-display`),
      document.querySelector(`[data-product-id="${productId}"].hybrid-star-rating`),
      document.querySelector(`[data-product-handle] .hybrid-star-rating[data-product-id="${productId}"]`)
    ].filter(Boolean);

    if (ratingElements.length === 0) return;

    const rating = stats.average_rating || 0;
    const count = stats.total_reviews || 0;

    ratingElements.forEach(ratingEl => {
      ratingEl.setAttribute('data-rating', rating);

      const stars = ratingEl.querySelector('.hybrid-stars__display') || ratingEl.querySelector('.stars');
      if (stars) {
        const starElements = stars.querySelectorAll('.hybrid-star, .star');
        starElements.forEach((star, index) => {
          if (index < Math.floor(rating)) {
            star.classList.add('filled');
          } else if (index < rating) {
            star.classList.add('half');
          } else {
            star.classList.remove('filled', 'half');
          }
        });
      }

      const reviewCount = ratingEl.querySelector('.hybrid-stars__text') || ratingEl.querySelector('.review-count');
      if (reviewCount) {
        if (count === 0) {
          reviewCount.innerHTML =
            '<span class="hybrid-stars__rating">0.0</span> <span class="hybrid-stars__count">(No reviews)</span>';
        } else {
          const ratingText = rating.toFixed(1);
          const countText = count === 1 ? '1 review' : `${count} reviews`;
          reviewCount.innerHTML = `<span class="hybrid-stars__rating">${ratingText}</span> <span class="hybrid-stars__count">(${countText})</span>`;
        }
      }
    });
  }

  initializeEventListeners(productId, widget) {
    if (!widget) return;

    this.attachSortListener(productId, widget);

    this.attachHistogramListeners(productId, widget);

    this.attachFormSubmissionHandler(productId, widget);

    this.attachWriteReviewHandler(widget);
  }

  attachSortListener(productId, widget) {
    const sortWrapper = widget.querySelector('.jdgm-rev-widg__sort-wrapper');
    if (!sortWrapper) {
      return;
    }

    const sortDropdown = sortWrapper.querySelector('.jdgm-sort-dropdown');
    if (!sortDropdown) {
      return;
    }

    const newDropdown = sortDropdown.cloneNode(true);
    sortDropdown.parentNode.replaceChild(newDropdown, sortDropdown);

    const currentDropdownValue = this.mapSortValueReverse(this.currentSort);
    newDropdown.value = currentDropdownValue;

    newDropdown.addEventListener(
      'change',
      async e => {
        e.preventDefault();
        e.stopPropagation();

        this.showSpinner(widget);

        try {
          const sortValue = this.mapSortValue(e.target.value);

          this.currentPage = 1;
          this.currentSort = sortValue;
          this.currentFilter = null;

          const histogramRows = widget.querySelectorAll('.jdgm-histogram__row');
          histogramRows.forEach(r => r.classList.remove('jdgm-histogram__row--selected'));

          const clearFilterRow = widget.querySelector('.jdgm-histogram__clear-filter');
          if (clearFilterRow) {
            clearFilterRow.style.display = 'none';
          }

          const stats = await this.fetchProductStats(productId);
          const reviews = await this.fetchProductReviews(productId, {
            sort: sortValue,
            page: 1,
            filter: null
          });

          await this.populateWidget(productId, reviews, stats, false);

          window.setTimeout(() => {
            this.attachHistogramListeners(productId, widget);
            this.attachSortListener(productId, widget);
          }, 750);
        } catch (_error) {
        } finally {
          this.hideSpinner(widget);
        }
      },
      true
    );
  }

  attachHistogramListeners(productId, widget) {
    const allHistogramRows = widget.querySelectorAll('.jdgm-histogram__row');
    allHistogramRows.forEach(row => {
      const rating = row.getAttribute('data-rating') || row.dataset.rating;
      if (this.currentFilter && rating === this.currentFilter) {
        row.classList.add('jdgm-histogram__row--selected');
      } else {
        row.classList.remove('jdgm-histogram__row--selected');
      }
      row.style.pointerEvents = '';
    });

    const clearFilterRow = widget.querySelector('.jdgm-histogram__clear-filter');
    if (clearFilterRow) {
      clearFilterRow.style.display = this.currentFilter ? 'block' : 'none';
    }

    let histogramRows = widget.querySelectorAll('.jdgm-histogram__row[data-rating]');

    histogramRows.forEach(row => {
      const newRow = row.cloneNode(true);
      row.parentNode.replaceChild(newRow, row);

      newRow.addEventListener(
        'click',
        async e => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const rating = newRow.getAttribute('data-rating') || newRow.dataset.rating || 'unknown';

          this.showSpinner(widget);

          try {
            let filterValue = null;

            if (rating === 'null' || this.currentFilter === rating) {
              filterValue = null;
              this.currentFilter = null;
            } else {
              filterValue = rating;
              this.currentFilter = rating;
            }

            const allRows = widget.querySelectorAll('.jdgm-histogram__row');
            allRows.forEach(r => r.classList.remove('jdgm-histogram__row--selected'));
            if (filterValue) {
              newRow.classList.add('jdgm-histogram__row--selected');
            }

            this.currentPage = 1;

            this.currentSort = 'newest';

            const stats = await this.fetchProductStats(productId);
            const reviews = await this.fetchProductReviews(productId, {
              filter: filterValue,
              sort: 'newest',
              page: 1
            });

            await this.populateWidget(productId, reviews, stats);

            const clearFilterRow = widget.querySelector('.jdgm-histogram__clear-filter');
            if (clearFilterRow) {
              if (filterValue) {
                clearFilterRow.style.display = 'block';
              } else {
                clearFilterRow.style.display = 'none';
              }
            }

            window.setTimeout(() => {
              this.attachHistogramListeners(productId, widget);
              this.attachSortListener(productId, widget);
            }, 750);
          } catch (_error) {
          } finally {
            this.hideSpinner(widget);
          }
        },
        true
      );
    });
  }

  cleanupDropdownOptions(widget) {
    if (!widget) return;

    const sortWrapper = widget.querySelector('.jdgm-rev-widg__sort-wrapper');
    if (!sortWrapper) return;

    const sortDropdown = sortWrapper.querySelector('.jdgm-sort-dropdown');
    if (!sortDropdown) return;

    const allowedOptions = ['most-recent', 'highest-rating', 'lowest-rating'];

    const options = Array.from(sortDropdown.options);

    options.forEach(option => {
      if (!allowedOptions.includes(option.value)) {
        option.remove();
      }
    });

    const dropdownValue = this.mapSortValueReverse(this.currentSort);
    sortDropdown.value = dropdownValue;
  }

  reattachHistogramListeners(productId, widget) {
    if (!widget) return;

    const histogramRows = widget.querySelectorAll('.jdgm-histogram__row');
    if (histogramRows.length === 0) return;

    histogramRows.forEach(row => {
      const newRow = row.cloneNode(true);
      row.parentNode.replaceChild(newRow, row);
    });

    const newHistogramRows = widget.querySelectorAll('.jdgm-histogram__row');

    newHistogramRows.forEach(row => {
      const rating = row.dataset.rating;

      row.addEventListener(
        'click',
        async e => {
          e.preventDefault();
          e.stopPropagation();

          this.showSpinner(widget);

          try {
            let filterValue = null;

            if (rating === 'null') {
              filterValue = null;
            } else if (this.currentFilter === rating) {
              filterValue = null;
              this.currentFilter = null;
            } else {
              filterValue = rating;
              this.currentFilter = rating;
            }

            newHistogramRows.forEach(r => r.classList.remove('jdgm-histogram__row--selected'));
            if (filterValue) {
              row.classList.add('jdgm-histogram__row--selected');
            }

            const stats = await this.fetchProductStats(productId);
            const reviews = await this.fetchProductReviews(productId, {
              filter: filterValue,
              forceRefresh: true
            });

            await this.populateWidget(productId, reviews, stats);

            window.setTimeout(() => {
              this.reattachHistogramListeners(productId, widget);
              this.attachSortListener(productId, widget);
            }, 750);
          } catch (_error) {
          } finally {
            this.hideSpinner(widget);
          }
        },
        true
      );
    });
  }

  mapSortValue(judgemeValue) {
    const mapping = {
      'most-recent': 'newest',
      'highest-rating': 'highest-rating',
      'lowest-rating': 'lowest-rating'
    };

    return mapping[judgemeValue] || 'newest';
  }

  mapSortValueReverse(internalValue) {
    const reverseMapping = {
      newest: 'most-recent',
      'highest-rating': 'highest-rating',
      'lowest-rating': 'lowest-rating'
    };

    return reverseMapping[internalValue] || 'most-recent';
  }

  showSpinner(widget) {
    const spinner = widget.querySelector('.jdgm-rev-widg__paginate-spinner-wrapper');
    if (spinner) {
      spinner.style.display = 'block';
    }
  }

  hideSpinner(widget) {
    const spinner = widget.querySelector('.jdgm-rev-widg__paginate-spinner-wrapper');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  attachWriteReviewHandler(widget) {
    const writeReviewLink = widget.querySelector('.jdgm-write-rev-link');
    const formWrapper = widget.querySelector('.jdgm-form-wrapper');

    if (!writeReviewLink || !formWrapper) return;

    const newLink = writeReviewLink.cloneNode(true);
    writeReviewLink.parentNode.replaceChild(newLink, writeReviewLink);

    newLink.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();

      const isExpanded = newLink.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        formWrapper.style.display = 'none';
        newLink.setAttribute('aria-expanded', 'false');
        newLink.textContent = 'Write a review';
      } else {
        formWrapper.style.display = 'block';
        newLink.setAttribute('aria-expanded', 'true');
        newLink.textContent = 'Cancel review';

        this.attachStarRatingHandlers(widget);

        const firstStar = formWrapper.querySelector('[data-alt="1"]');
        if (firstStar) {
          firstStar.focus();
        }
      }
    });

    this.attachCancelButtonHandler(widget);
  }

  attachCancelButtonHandler(widget) {
    const cancelButton = widget.querySelector('.jdgm-cancel-rev');
    const formWrapper = widget.querySelector('.jdgm-form-wrapper');
    const writeReviewLink = widget.querySelector('.jdgm-write-rev-link');

    if (!cancelButton || !formWrapper) return;

    const newCancelButton = cancelButton.cloneNode(true);
    cancelButton.parentNode.replaceChild(newCancelButton, cancelButton);

    newCancelButton.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();

      formWrapper.style.display = 'none';

      if (writeReviewLink) {
        writeReviewLink.setAttribute('aria-expanded', 'false');
        writeReviewLink.textContent = 'Write a review';
      }
    });
  }

  attachStarRatingHandlers(widget) {
    const ratingContainer = widget.querySelector('.jdgm-form__rating');
    if (!ratingContainer) return;

    const stars = ratingContainer.querySelectorAll('.jdgm-star');
    const scoreInput = ratingContainer.querySelector('input[name="score"]');

    if (!stars.length || !scoreInput) return;

    stars.forEach((star, index) => {
      const rating = index + 1;

      star.addEventListener('mouseenter', () => {
        stars.forEach((s, i) => {
          if (i <= index) {
            s.classList.remove('jdgm--off');
            s.classList.add('jdgm--on');
          } else {
            s.classList.remove('jdgm--on');
            s.classList.add('jdgm--off');
          }
        });
      });

      star.addEventListener('click', e => {
        e.preventDefault();

        scoreInput.value = rating;

        this.clearValidationError(scoreInput);

        stars.forEach((s, i) => {
          if (i < rating) {
            s.classList.remove('jdgm--off');
            s.classList.add('jdgm--on');
            s.setAttribute('data-selected', 'true');
          } else {
            s.classList.remove('jdgm--on');
            s.classList.add('jdgm--off');
            s.removeAttribute('data-selected');
          }
        });

        ratingContainer.setAttribute('data-rating-selected', rating);
      });
    });

    ratingContainer.addEventListener('mouseleave', () => {
      const selectedRating = scoreInput.value;
      if (selectedRating) {
        stars.forEach((s, i) => {
          if (i < parseInt(selectedRating)) {
            s.classList.remove('jdgm--off');
            s.classList.add('jdgm--on');
          } else {
            s.classList.remove('jdgm--on');
            s.classList.add('jdgm--off');
          }
        });
      } else {
        stars.forEach(s => {
          s.classList.remove('jdgm--on');
          s.classList.add('jdgm--off');
        });
      }
    });

    stars.forEach(star => {
      star.setAttribute('tabindex', '0');
      star.setAttribute('role', 'button');

      star.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          star.click();
        }
      });
    });
  }

  attachFormSubmissionHandler(productId, widget) {
    const form = widget.querySelector('.jdgm-form');
    if (!form) return;

    const newForm = this.prepareForm(form);
    form.parentNode.replaceChild(newForm, form);

    newForm.addEventListener('submit', async e => {
      await this.handleFormSubmit(e, newForm, productId, widget);
    });

    this.attachStarRatingHandlers(widget);
  }

  prepareForm(form) {
    const newForm = form.cloneNode(true);
    const dropdown = newForm.querySelector('select[name="reviewer_name_format"]');

    if (dropdown && dropdown.options.length === 0) {
      this.populateNameFormatDropdown(dropdown);
    }

    const emailField = newForm.querySelector('input[name="reviewer_email"]');
    if (emailField && emailField.disabled) {
      emailField.disabled = false;
      emailField.removeAttribute('disabled');
      emailField.readOnly = true;
      emailField.setAttribute('readonly', 'readonly');
    }

    return newForm;
  }

  populateNameFormatDropdown(dropdown) {
    const jdgmSettings = window.jdgmSettings || {};
    const defaultName = jdgmSettings.default_reviewer_name || 'John Smith';
    const anonymousText = jdgmSettings.widget_reviewer_anonymous || 'Anonymous';

    const nameParts = defaultName.split(' ');
    const firstName = nameParts[0] || 'John';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] + '.' : 'S.';
    const firstInitial = firstName[0] + '.';
    const lastNameInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] + '.' : 'S.';

    dropdown.innerHTML = `
      <option value="" selected="">${defaultName}</option>
      <option value="last_initial">${firstName} ${lastInitial}</option>
      <option value="first_name_only">${firstName}</option>
      <option value="all_initials">${firstInitial}${lastNameInitial}</option>
      <option value="anonymous">${anonymousText}</option>
    `;
  }

  async handleFormSubmit(e, form, productId, widget) {
    e.preventDefault();
    e.stopPropagation();

    this.clearAllValidationErrors(form);

    const formData = new FormData(form);
    const validationErrors = this.validateForm(formData, form);

    if (validationErrors.length > 0) {
      validationErrors.forEach(error => {
        this.showValidationError(form, error.fieldName, error.errorMessage, error.inputElement);
      });
      return;
    }

    const reviewData = this.buildReviewData(formData, productId);
    const submitButton = form.querySelector('.jdgm-submit-rev');

    this.setButtonLoading(submitButton, true);

    try {
      const success = await this.submitToJudgeMe(reviewData);

      if (success) {
        this.handleSubmissionSuccess(form, widget);
      } else {
        this.handleSubmissionError();
      }
    } catch (error) {
      this.logError(error, {
        method: 'handleFormSubmit',
        productId: productId,
        severity: 'critical'
      });
      this.handleSubmissionError();
    } finally {
      this.setButtonLoading(submitButton, false);
    }
  }

  buildReviewData(formData, productId) {
    const reviewData = {
      shop_domain: window.HYBRID_REVIEWS_CONFIG?.judgemeShopDomain || 'baby-formula-store.myshopify.com',
      platform_product_id: productId.toString(),
      email: formData.get('reviewer_email'),
      name: formData.get('reviewer_name'),
      rating: parseInt(formData.get('score')),
      title: formData.get('review_title') || '',
      body: formData.get('review_body')
    };

    const nameFormat = formData.get('reviewer_name_format');
    if (nameFormat) {
      reviewData.reviewer_name_format = nameFormat;
    }

    if (this.verificationToken) {
      reviewData.token = this.verificationToken;
    }

    if (this.orderId) {
      reviewData.order_id = this.orderId;
    }

    return reviewData;
  }

  setButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.originalText = button.value;
      button.value = 'Submitting...';
      button.disabled = true;
    } else {
      button.value = button.dataset.originalText || 'Submit Review';
      button.disabled = false;
    }
  }

  handleSubmissionSuccess(form, widget) {
    this.showSuccessMessage(widget);
    form.reset();

    const formWrapper = widget.querySelector('.jdgm-form-wrapper');
    if (formWrapper) {
      formWrapper.style.display = 'none';
    }

    setTimeout(() => {
      const notification = widget.querySelector('.jdgm-notification');
      if (notification) {
        notification.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.scrollBy({ top: -20, behavior: 'smooth' });
      } else {
        const header = widget.querySelector('.jdgm-rev-widg__header');
        if (header) {
          header.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
  }

  handleSubmissionError() {
    alert('There was an error submitting your review. Please try again.');
  }

  async submitToJudgeMe(reviewData) {
    const simulateMode = window.HYBRID_REVIEWS_CONFIG?.simulateSubmission || false;

    if (simulateMode) {
      const isDebug = window.HYBRID_REVIEWS_CONFIG?.debug || false;
      if (isDebug) {
        console.log('🔵 SIMULATION MODE: Review submission simulated (not sent to Judge.me)');
        console.log('Review data that would be submitted:', reviewData);
      }

      await new Promise(resolve => setTimeout(resolve, this.ANIMATION_DELAY_LONG));

      if (isDebug) {
        console.log('✅ Simulated success response from Judge.me');
      }
      return true;
    }

    try {
      // Endpoint: https://api.judge.me/api/v1/reviews
      const formData = new FormData();

      formData.append('url', reviewData.shop_domain);
      formData.append('shop_domain', reviewData.shop_domain);
      formData.append('platform', 'shopify');
      formData.append('id', reviewData.platform_product_id);
      formData.append('email', reviewData.email);
      formData.append('name', reviewData.name);
      formData.append('rating', reviewData.rating);
      formData.append('title', reviewData.title || '');
      formData.append('body', reviewData.body || '');

      if (reviewData.reviewer_name_format) {
        formData.append('reviewer_name_format', reviewData.reviewer_name_format);
      }
      if (reviewData.token) {
        formData.append('token', reviewData.token);
      }
      if (reviewData.order_id) {
        formData.append('order_id', reviewData.order_id);
      }

      const isDebug = window.HYBRID_REVIEWS_CONFIG?.debug || false;
      if (isDebug) {
        console.log('🟢 LIVE MODE: Submitting review');
      }

      const response = await fetch('https://api.judge.me/api/v1/reviews', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const responseText = await response.text();
        if (isDebug) {
          console.error('API error:', response.status, responseText);
        }
        return false;
      }

      const responseData = await response.json();
      if (isDebug) {
        console.log('✅ Review submitted successfully:', responseData.message);
      }
      return true;
    } catch (error) {
      this.logError(error, {
        method: 'submitToJudgeMe',
        reviewData: {
          productId: reviewData.platform_product_id,
          rating: reviewData.rating
        },
        severity: 'critical'
      });
      console.error('Review submission error:', error);
      return false;
    }
  }

  showSuccessMessage(widget) {
    const simulateMode = window.HYBRID_REVIEWS_CONFIG?.simulateSubmission || false;

    const notificationWrapper = document.createElement('div');
    notificationWrapper.className = 'jdgm-notification';

    const simulationNotice = simulateMode
      ? '<br><span style="color: #0066cc; font-weight: bold;">⚠️ SIMULATION MODE: Review was NOT sent to Judge.me</span>'
      : '';

    notificationWrapper.innerHTML = `
      <div class="jdgm-notification__inner">
        <div class="jdgm-notification__title">Review Submitted!</div>
        Thank you! Please refresh the page in a few moments to see your review. You can remove or edit your review by logging into <a href="https://judge.me/login" target="_blank" rel="nofollow noopener">Judge.me</a>
        ${simulationNotice}
      </div>
    `;

    const revWidg = widget.querySelector('.jdgm-rev-widg');
    const revWidgHeader = widget.querySelector('.jdgm-rev-widg__header');
    const formWrapper = widget.querySelector('.jdgm-form-wrapper');

    if (!revWidg) {
      return;
    }

    const existingNotification = widget.querySelector('.jdgm-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    if (revWidgHeader) {
      revWidgHeader.appendChild(notificationWrapper);
    } else {
      revWidg.appendChild(notificationWrapper);
    }

    const writeReviewLink = widget.querySelector('.jdgm-write-rev-link');
    if (writeReviewLink) {
      writeReviewLink.textContent = 'Refresh page';

      const newLink = writeReviewLink.cloneNode(true);
      writeReviewLink.parentNode.replaceChild(newLink, writeReviewLink);

      newLink.addEventListener('click', e => {
        e.preventDefault();
        window.location.reload();
      });
    }
  }

  validateForm(formData, form) {
    const errors = [];

    const rating = formData.get('score');
    if (!rating) {
      const scoreInput = form.querySelector('input[name="score"]');
      errors.push({
        fieldName: 'score',
        errorMessage: 'This field is required.',
        inputElement: scoreInput
      });
    }

    const reviewBody = formData.get('review_body');
    if (!reviewBody || reviewBody.trim() === '') {
      const bodyInput = form.querySelector('textarea[name="review_body"]');
      errors.push({
        fieldName: 'review_body',
        errorMessage: 'This field is required.',
        inputElement: bodyInput
      });
    }

    const name = formData.get('reviewer_name');
    if (!name || name.trim() === '') {
      const nameInput = form.querySelector('input[name="reviewer_name"]');
      errors.push({
        fieldName: 'reviewer_name',
        errorMessage: 'This field is required.',
        inputElement: nameInput
      });
    }

    const email = formData.get('reviewer_email');
    const emailInput = form.querySelector('input[name="reviewer_email"]');

    if (!email || email.trim() === '') {
      errors.push({
        fieldName: 'reviewer_email',
        errorMessage: 'This field is required.',
        inputElement: emailInput
      });
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push({
          fieldName: 'reviewer_email',
          errorMessage: 'Please enter a valid email address.',
          inputElement: emailInput
        });
      }
    }

    return errors;
  }

  showValidationError(form, fieldName, errorMessage, inputElement) {
    if (!inputElement) return;

    inputElement.classList.add('jdgm-input-error');
    inputElement.setAttribute('aria-invalid', 'true');

    const errorId = `${inputElement.id}-error`;
    const errorDiv = document.createElement('div');
    errorDiv.id = errorId;
    errorDiv.className = 'jdgm-input-error';
    errorDiv.textContent = errorMessage;

    inputElement.setAttribute('aria-describedby', errorId);
    inputElement.parentNode.insertBefore(errorDiv, inputElement.nextSibling);

    const blurHandler = () => {
      this.validateFieldOnBlur(inputElement, fieldName, form);
    };
    inputElement.addEventListener('blur', blurHandler);

    const inputHandler = () => {
      this.validateFieldOnInput(inputElement, fieldName, form);
    };
    inputElement.addEventListener('input', inputHandler);

    inputElement._validationHandlers = { blur: blurHandler, input: inputHandler };
  }

  clearAllValidationErrors(form) {
    form.querySelectorAll('.jdgm-input-error[id$="-error"]').forEach(errorDiv => {
      errorDiv.remove();
    });

    form.querySelectorAll('input.jdgm-input-error, textarea.jdgm-input-error').forEach(input => {
      input.classList.remove('jdgm-input-error');
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');

      if (input._validationHandlers) {
        input.removeEventListener('blur', input._validationHandlers.blur);
        input.removeEventListener('input', input._validationHandlers.input);
        delete input._validationHandlers;
      }
    });
  }

  clearValidationError(inputElement) {
    if (!inputElement) return;

    inputElement.classList.remove('jdgm-input-error');
    inputElement.removeAttribute('aria-invalid');
    inputElement.removeAttribute('aria-describedby');

    const errorId = `${inputElement.id}-error`;
    const errorDiv = document.getElementById(errorId);
    if (errorDiv) {
      errorDiv.remove();
    }
  }

  validateFieldOnBlur(inputElement, fieldName, form) {
    const value = inputElement.value.trim();

    let isValid = true;
    let errorMessage = '';

    if (fieldName === 'reviewer_email') {
      if (!value) {
        isValid = false;
        errorMessage = 'This field is required.';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          isValid = false;
          errorMessage = 'Please enter a valid email address.';
        }
      }
    } else if (fieldName === 'reviewer_name' || fieldName === 'review_body') {
      if (!value) {
        isValid = false;
        errorMessage = 'This field is required.';
      }
    }

    if (!isValid) {
      this.clearValidationError(inputElement);
      this.showValidationError(form, fieldName, errorMessage, inputElement);
    }
  }

  validateFieldOnInput(inputElement, fieldName, form) {
    const value = inputElement.value.trim();

    let isValid = false;
    let errorMessage = '';

    if (fieldName === 'reviewer_email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value) {
        return;
      } else if (!emailRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address.';
      } else {
        isValid = true;
      }
    } else if (fieldName === 'reviewer_name' || fieldName === 'review_body') {
      isValid = value.length > 0;
    } else if (fieldName === 'score') {
      isValid = value.length > 0;
    }

    if (isValid) {
      this.clearValidationError(inputElement);
    } else if (errorMessage) {
      this.clearValidationError(inputElement);
      this.showValidationError(form, fieldName, errorMessage, inputElement);
    }
  }
}

window.getHybridReviewsErrors = function () {
  if (!window.HybridReviews) {
    return [];
  }
  return window.HybridReviews.errorLog.map(err => ({
    time: err.timestamp,
    method: err.method,
    message: err.message,
    severity: err.severity,
    context: err
  }));
};

window.HybridReviews = new HybridReviews();

document.addEventListener('DOMContentLoaded', async function () {
  const testimonialsRating = document.querySelector('.testimonials__rating--filled');
  const testimonialsText = document.querySelector('.testimonials__rating--unfilled + p');

  if (testimonialsRating && testimonialsText && window.HybridReviews) {
    if (!window.HybridReviews.supabaseUrl || !window.HybridReviews.supabaseKey) {
      return;
    }

    try {
      const response = await fetch(
        // REQ-017 FR-005: total_reviews was dropped from review_stats (Jan 2026); published_reviews is the live count
        `${window.HybridReviews.supabaseUrl}/rest/v1/review_stats?select=published_reviews,average_rating`,
        {
          headers: {
            apikey: window.HybridReviews.supabaseKey,
            Authorization: `Bearer ${window.HybridReviews.supabaseKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data && data.length > 0) {
        let totalReviews = 0;
        let totalRatingSum = 0;

        data.forEach(stat => {
          if (stat.published_reviews > 0) {
            totalReviews += stat.published_reviews;
            totalRatingSum += stat.average_rating * stat.published_reviews;
          }
        });

        const averageRating = totalReviews > 0 ? totalRatingSum / totalReviews : 0;

        const percentage = (averageRating / window.HybridReviews.MAX_RATING) * 100;
        testimonialsRating.style.width = `${percentage}%`;

        const reviewText = totalReviews === 1 ? 'review' : 'reviews';
        testimonialsText.textContent = `Based on ${totalReviews} verified ${reviewText}`;
      } else {
        testimonialsRating.style.width = '0%';
        testimonialsText.textContent = 'Be the first to review our products!';
      }
    } catch (_error) {}
  }
});

document.addEventListener('DOMContentLoaded', function () {
  const collectionRatings = document.querySelectorAll('.hybrid-collection-rating');

  if (collectionRatings.length > 0 && window.HybridReviews) {
    const productIds = Array.from(collectionRatings).map(el => el.dataset.productId);

    window.HybridReviews.fetchBatchProductStats(productIds)
      .then(statsMap => {
        collectionRatings.forEach(ratingEl => {
          const productId = ratingEl.dataset.productId;
          const stats = statsMap[productId];

          if (stats && stats.total_reviews >= 0) {
            const starsActive = ratingEl.querySelector('.hybrid-stars__active');
            if (starsActive) {
              const rating = stats.average_rating || 0;
              const percentage = (rating / window.HybridReviews.MAX_RATING) * 100;
              starsActive.style.width = `${percentage}%`;
            }

            const countEl = ratingEl.querySelector('.hybrid-stars__count');
            if (countEl) {
              const count = stats.total_reviews || 0;
              const countText = count === 1 ? '1 review' : `${count} reviews`;
              countEl.textContent = count > 0 ? `(${countText})` : '(No reviews)';
            }

            ratingEl.style.display = 'block';
          }
        });
      })
      .catch(_error => {});
  }
});
